import { useState, useCallback, useRef } from 'react';
import { buildPrompt, PromptBuildRequest, PromptBuildResponse } from '../api/promptManagement';

export interface DynamicPromptConfig {
  system_prompt?: string;
  user_prompt?: string;
  metadata?: Record<string, any>;
  warnings?: string[];
  isLoading: boolean;
  error: string | null;
  assetType: string;
  projectId?: string;
}

export interface PromptCache {
  [key: string]: DynamicPromptConfig;
}

export const useDynamicPrompts = () => {
  const [promptCache, setPromptCache] = useState<PromptCache>({});
  const promptCacheRef = useRef<PromptCache>({});
  
  // Keep ref in sync with state
  promptCacheRef.current = promptCache;

  const getPromptForProject = useCallback(async (
    assetType: string,
    projectId?: string,
    context?: Record<string, any>
  ): Promise<DynamicPromptConfig> => {
    const cacheKey = `${assetType}-${projectId || 'default'}`;
    
    console.log(`🔧 getPromptForProject called for ${assetType}${projectId ? ` (project: ${projectId})` : ' (default)'}, cacheKey: ${cacheKey}`);
    
    // Check if we already have this prompt cached
    if (promptCacheRef.current[cacheKey] && !promptCacheRef.current[cacheKey].isLoading) {
      console.log(`📋 Using cached prompt for ${cacheKey}`);
      return promptCacheRef.current[cacheKey];
    }

    // Set loading state
    setPromptCache(prev => ({
      ...prev,
      [cacheKey]: {
        isLoading: true,
        error: null,
        assetType,
        projectId
      }
    }));

    try {
      // Always use the build API - it will handle both project-specific and default prompts
      const buildRequest: PromptBuildRequest = {
        prompt_type: assetType === 'image' ? 'image' : 'video',
        context: {
          asset_type: assetType,
          ...context
        }
      };

      // Only add project_id if it's provided
      if (projectId) {
        buildRequest.project_id = projectId;
        buildRequest.context = {
          ...buildRequest.context,
          project_id: projectId
        };
      }

      console.log(`🔧 Building prompt for ${assetType}${projectId ? ` (project: ${projectId})` : ' (default)'}`, buildRequest);

      const response: PromptBuildResponse = await buildPrompt(buildRequest);
      
      const promptConfig: DynamicPromptConfig = {
        system_prompt: response.system_prompt,
        user_prompt: response.user_prompt,
        metadata: response.metadata,
        warnings: response.warnings,
        isLoading: false,
        error: null,
        assetType,
        projectId
      };

      console.log(`✅ Prompt built successfully for ${assetType}${projectId ? ` (project: ${projectId})` : ' (default)'}`, {
        system_prompt_length: response.system_prompt?.length || 0,
        user_prompt_length: response.user_prompt?.length || 0,
        metadata: response.metadata
      });

      // Cache the result
      setPromptCache(prev => ({
        ...prev,
        [cacheKey]: promptConfig
      }));

      return promptConfig;

    } catch (error) {
      console.error(`Failed to load prompt for ${assetType} (project: ${projectId}):`, error);
      
      const errorConfig: DynamicPromptConfig = {
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load prompt',
        assetType,
        projectId
      };

      // Cache the error state
      setPromptCache(prev => ({
        ...prev,
        [cacheKey]: errorConfig
      }));

      return errorConfig;
    }
  }, []);

  const getPromptForAsset = useCallback(async (
    assetType: string,
    projectId?: string,
    context?: Record<string, any>
  ): Promise<DynamicPromptConfig> => {
    // Always use getPromptForProject - it handles both project-specific and default prompts
    // The build API will automatically fall back to defaults if no project-specific prompt exists
    return getPromptForProject(assetType, projectId, context);
  }, [getPromptForProject]);

  const clearCache = useCallback(() => {
    setPromptCache({});
  }, []);

  const refreshPrompt = useCallback(async (
    assetType: string,
    projectId?: string,
    context?: Record<string, any>
  ): Promise<DynamicPromptConfig> => {
    const cacheKey = `${assetType}-${projectId || 'default'}`;
    
    console.log(`🔄 Refreshing prompt for ${cacheKey}`);
    
    // Remove from cache to force refresh
    setPromptCache(prev => {
      const newCache = { ...prev };
      delete newCache[cacheKey];
      return newCache;
    });

    // Reload the prompt
    return getPromptForProject(assetType, projectId, context);
  }, [getPromptForProject]);

  return {
    // State
    promptCache,
    
    // Actions
    getPromptForProject,
    getPromptForAsset,
    clearCache,
    refreshPrompt
  };
}; 